import * as tslib_1 from "tslib";
import { catchError, map } from "rxjs/operators";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";
import * as i0 from "@angular/core";
import * as i1 from "../../shared/services/api.service";
import * as i2 from "../../shared/services/helper.service";
/** Base path of the document instances API */
var BASE_PATH = "document-archive-docs";
/** The whole API is v2-only: force it regardless of the environment default */
var API_VERSION = "v2";
/**
 * Front-end API layer of the document instances (/api/v2/document-archive-docs): the
 * documents a property actually owns.
 *
 * Reads require `canReadDocumentsList` on the CURRENTLY SELECTED role.
 *
 * Writes require `canCreateDocuments` (`canEditDocumentsData` for the data of an existing
 * document) and go to THESE collections only: the legacy archive has its own endpoints on its
 * own collections, and it never reads what is written here. The other way round is not true until
 * the switch: the back-end realigns these collections to the legacy ones (`archive-alignment`, BE
 * README §4.64, rebuild and swap done on 27 Oct 2026, nightly `sync` to be scheduled), and a row
 * written here with no legacy twin is removed by it. Verified on the back-end code on 2026-10-28.
 *
 * @since 3.0.0
 */
var DocumentArchiveDocsService = /** @class */ (function () {
    /**
     * Class constructor
     */
    function DocumentArchiveDocsService(apiService, helperService) {
        this.apiService = apiService;
        this.helperService = helperService;
    }
    /**
     * One page of the documents of an archive.
     *
     * `documentArchiveId` is mandatory: there is no global listing, and there will not be
     * one, because the owning company is two hops away and could not be scoped per tenant.
     *
     * Every filter is resolved by the back-end. Pass them through rather than narrowing the
     * result afterwards: the list is already the selection the back-end made.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.list = function (params) {
        return this.call("GET", BASE_PATH, this.buildListParams(params));
    };
    /**
     * A single document, soft deleted ones included: the restore screen has to be able to
     * read what it is about to bring back.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.get = function (docId) {
        return this.call("GET", BASE_PATH + "/" + docId);
    };
    /**
     * Archive a document — the second of the two calls of an insert, the first being
     * `POST /files` (@see FileService.uploadImportingFiles), which is unchanged.
     *
     * Requires `canCreateDocuments`. It writes the 3.0.0 collections and it is NOT the legacy
     * `PATCH /document-archives/:id/insert-multi-docs`, which writes the other ones: a document
     * inserted here does not show up in the legacy dossier, so one screen has to use one of the
     * two and never both.
     *
     * ONE call archives ONE document, over as many rows as the types × elements it names, all
     * sharing one barcode. Around the write the back-end computes the expiry, re-runs the rules that
     * act on OTHER documents and expires the previous copy of the most-recent-only types; billing and
     * the supplier counter wait for a back-end switch that is off until the cut-over. The detail is on
     * @see DocumentArchiveDocInsertPayload (verified on the back-end code on 2026-10-28).
     *
     * On success RELOAD THE TREE: the report is not enough to patch the dossier in place,
     * because numbering, counters and grouping are composed server side.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.insertMultiDocs = function (payload) {
        return this.call("POST", BASE_PATH + "/insert-multi-docs", payload);
    };
    /**
     * Register a PHYSICAL delivery: a folder arrives with its printed barcode label, its documents
     * show up in the archive straight away, and the PDFs are scanned later.
     *
     * ONE call, not two: there is no `POST /files` before it, because the bytes do not exist yet.
     *
     * The document is declared as the PARTS it will be scanned into, one QR label each, and
     * `data.numFiles` has to EQUAL how many there are — derive it from the list and the 400 that
     * enforces it is unreachable. @see DocumentArchiveDocPaperInsertData
     *
     * It also opens the entry on the DELIVERY NOTE of the day, which is the work list of the scanning
     * team — which is why it is back-office only: `canCreateDocuments` AND a role of type `admin` or
     * `operator`. A customer role holding the permission gets a 403, so the button has to be hidden
     * for non staff rather than left to fail.
     *
     * TWO ANSWERS THE CALLER MUST TELL APART:
     *  - 409, the barcode is already filed in this archive. It is the only error asking the operator
     *    for a PHYSICAL action — a different label — so it must not be retried with the same code, and
     *    must not be reported as a generic failure. The barcode is what the scanner follows to attach
     *    the PDFs, and two documents sharing one would let a scan land on the wrong document;
     *  - everything else (400 / 403 / 404), which is a form or a permission problem. Nothing was
     *    written in either case: every check runs before the first write.
     *
     * On success RELOAD THE TREE, same as the digital insert. @see DocumentArchiveDocPaperInsertResult
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.insertMultiPaperDocs = function (payload) {
        return this.call("POST", BASE_PATH + "/insert-multi-paper-docs", payload);
    };
    /**
     * File VIRTUAL documents (NEW-M44, FE-M5): one new row per type and element, marked `mockup`, with
     * the form and neither a file nor a code. @see DocumentArchiveDocVirtualInsertPayload
     *
     * Admin and operator only: the back-end refuses any other role with a 403. NOT idempotent: the
     * caller keeps its button off while the call is out, or a second click writes the rows twice.
     *
     * On success RELOAD THE TREE, as after the other inserts. Errors are NOT handled here (@see call):
     * the caller notifies the user.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.insertMultiVirtualDocs = function (payload) {
        return this.call("POST", BASE_PATH + "/insert-multi-virtual-docs", payload);
    };
    /**
     * Send the CANCELLATION of one or more contracts.
     *
     * Requires `canCreateDocuments`. It writes the 3.0.0 collections and is NOT the legacy
     * `PATCH /document-archives/:id/cancel-contract`, which writes the other ones — the legacy dossier
     * keeps calling that one, and never reads what this one writes (@see DocumentArchiveDocsService on
     * the realignment of these collections until the switch).
     *
     * ⚠️ TWO ACTIONS BEHIND ONE ENDPOINT, chosen by `message.cancellationType`: `"immediate"` expires
     * the contracts now and reopens the empty obligation behind them, anything else only records the
     * communication and leaves the expiry alone. @see DocumentArchiveContractCancellationMessage
     *
     * This call RECORDS the cancellation; it sends no mail. The mail goes out BEFORE it, from the
     * dialog, with `POST /communications` (@see DocumentArchiveCancelContractDialogComponent.send).
     * The back-end then renders the message to a PDF, files it on the rows (`pdfmsg`), stamps who asked
     * and when, and switches off any automatic renewal; an immediate cancellation also expires the rows
     * and reopens their obligation. No document link is built into the body — this page does not know
     * file paths (@see downloadFile). Verified on the code of both sides on 2026-10-28.
     *
     * The answer is a REPORT: an unknown id does not fail the call, it comes back counted in
     * `skipped`. @see DocumentArchiveContractActionResult
     *
     * On any answer RELOAD THE TREE: an immediate cancellation can open a mandatory row that was not
     * on the page before.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.cancelContract = function (payload) {
        return this.call("POST", BASE_PATH + "/cancel-contract", payload);
    };
    /**
     * Expire one or more contracts, with no communication attached.
     *
     * Requires `canCreateDocuments`. Same report and same reload duty as the cancellation, and the
     * same authorisation boundary on `documentArchiveId`.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.expireContract = function (payload) {
        return this.call("POST", BASE_PATH + "/expire-contract", payload);
    };
    /**
     * Switch a fire safety obligation off: the row is excluded, deleted, and the periodic renewal is
     * opened behind it.
     *
     * Requires `canCreateDocuments`, and accepts ONLY the three document types of
     * @see FIRE_SAFETY_DISABLE_TITLES — anything else answers 400, so the caller gates the action on
     * the document type instead of letting it fail.
     *
     * NO BODY: the row alone says everything the back-end needs. `docId` is the `_id` of a row of the
     * 3.0.0 archive, as the tree serves it.
     *
     * The answer is a report to be READ and not an acknowledgement (@see
     * DocumentArchiveDocDisableResult): `excluded: false` means it was already on record, and
     * `replacementDocId: null` is a legitimate answer rather than a failure. RELOAD THE TREE either
     * way — the row disappears and a new obligation can have appeared.
     *
     * Errors are NOT handled here: the caller reports them.
     *
     * @since 3.0.0
     *
     * @param docId The row to switch off
     */
    DocumentArchiveDocsService.prototype.disable = function (docId) {
        return this.call("PATCH", BASE_PATH + "/" + docId + "/disable");
    };
    /**
     * Update a document. Every key is optional, and `documentArchiveId` / `propertyId` are
     * rejected by the back-end: moving a row across archives would move it across properties.
     *
     * @since 3.0.0
     *
     * @param docId   The document to update
     * @param payload The keys to change — `data` above all
     */
    DocumentArchiveDocsService.prototype.update = function (docId, payload) {
        return this.call("PUT", BASE_PATH + "/" + docId, payload);
    };
    /**
     * Save the "Modifica dati aggiuntivi" dialog: the form, the files, or both, on one or more rows
     * of the same document. The dialog uses this for EVERY save, and no longer the PUT above.
     *
     * Requires `canCreateDocuments`. Every check runs before the first write, so a 4xx writes
     * nothing; a 200 is a REPORT to read, because the edit is not atomic across rows
     * (@see DocumentArchiveDocUpdateDocsResult). Reload the tree after it either way.
     *
     * Errors are NOT handled here: the caller reports them with its own Italian texts, since the
     * back-end messages are in English.
     *
     * @since 3.0.0
     *
     * @param payload The rows, the form and the file changes @see DocumentArchiveDocUpdateDocsPayload
     */
    DocumentArchiveDocsService.prototype.updateDocs = function (payload) {
        return this.call("POST", BASE_PATH + "/update-docs", payload);
    };
    /**
     * The parts of a paper document, with where each one stands (FEX-08): read off the SAME delivery
     * note entry `POST /paper-parts` writes, the oldest live one of the code on the property of the
     * archive. Not `GET ddts/ddt-documents/:code`, which reads the first entry of the code, deleted or
     * of another property, and checks no company (decision of 28 October 2026).
     *
     * Platform staff only: a role type other than admin and operator gets a 403. The back-end
     * upper-cases the code. Errors are NOT handled here: the window tells them apart.
     *
     * @since 3.0.0
     *
     * @param documentArchiveId The archive of the row: the authorisation boundary of the read
     * @param barCode           The code of the paper document
     */
    DocumentArchiveDocsService.prototype.paperParts = function (documentArchiveId, barCode) {
        return this.call("GET", BASE_PATH + "/paper-parts", { documentArchiveId: documentArchiveId, barCode: barCode });
    };
    /**
     * Change the parts of a paper document (FEX-08, NEW-A6): the FINAL list, written on every live row
     * of the code and on its delivery note entry. The back-end names the parts and answers the labels
     * to print.
     *
     * NEVER BEFORE THE SWITCH: the delivery note entry is shared with the legacy archive, while the
     * rows of this one are realigned on the legacy every night. The only caller is the parts window,
     * opened behind `archiveV3Live` (@see DocumentArchiveRowActionsService.openPaperParts).
     *
     * Not idempotent: the list is the final one, so the same body sent again after a success may be
     * refused or may change the parts again. Compose the next list from `data.parts` of the answer.
     * Errors are NOT handled here: the window reports them with its own Italian texts.
     *
     * @since 3.0.0
     *
     * @param payload The archive, the code and the final list of the parts
     */
    DocumentArchiveDocsService.prototype.updatePaperParts = function (payload) {
        return this.call("POST", BASE_PATH + "/paper-parts", payload);
    };
    /**
     * Soft delete a set of documents, and mark them for the retention cron.
     *
     * Nothing is physically removed and the rows stay restorable through
     * `PATCH /document-archive-docs/:docId/restore`.
     *
     * `documentArchiveId` is not a convenience filter, it is the AUTHORISATION BOUNDARY: an id
     * belonging to another archive is not touched and comes back among the skipped ones.
     *
     * The answer is a REPORT, not an acknowledgement: the operation is not atomic, so part of the
     * list can succeed while the rest is skipped, and the caller has to read it.
     * @see DocumentArchiveDocBulkDeleteResult
     *
     * Used for a single document too: one code path, and the same report to read.
     *
     * @since 3.0.0
     *
     * @param documentArchiveId The archive the documents belong to
     * @param docIds            Up to 100 ids
     */
    DocumentArchiveDocsService.prototype.deleteBulk = function (documentArchiveId, docIds) {
        return this.call("DELETE", BASE_PATH + "/bulk", {
            documentArchiveId: documentArchiveId,
            docs: (docIds || []).join(","),
        });
    };
    /**
     * The bytes of a file attached to a document.
     *
     * `fileId` is the `filename` of the entry in `data.document[]` — the archiving name, not a
     * position: removing an entry shifts every following one, so an index kept from before would
     * fetch the wrong file. The entries carry no `_id` and cannot: `data` is a schemaless object,
     * so MongoDB generates none.
     *
     * The front-end knows nothing about paths anymore: it asks for a file by id and the back-end
     * resolves it. That is what removed the hand written host, the double host on the historical
     * rows, and the choice between `data.document` and `data.documentFile`.
     *
     * Errors arrive as a BLOB, because that is what was asked for: `parseErrorBlob` turns them
     * back into the usual envelope before they reach the caller.
     *
     * @since 3.0.0
     *
     * @param docId  The document holding the file
     * @param fileId `filename` of the entry in `data.document[]`
     */
    DocumentArchiveDocsService.prototype.downloadFile = function (docId, fileId) {
        var _this = this;
        return this.apiService
            .apiCall("GET", BASE_PATH + "/" + docId + "/files/" + encodeURIComponent(fileId), {}, { responseType: "blob" }, undefined, undefined, API_VERSION)
            .pipe(catchError(function (err) { return _this.helperService.parseErrorBlob(err); }));
    };
    /**
     * Every file of one document as a single zip, built by the back-end ("Scarica tutti").
     *
     * The whole response is observed, not only the body: the zip name travels in
     * `Content-Disposition` and the number of files left out in `X-Archive-Skipped-Files`.
     *
     * Unlike `downloadFile`, errors are NOT passed through `parseErrorBlob`: that one drops the HTTP
     * status, which is what the dialog picks its text from, and never settles when the body is not
     * JSON. Note that ApiService emits a failure without status (network down, connection cut once
     * the zip has started) as an `Error` VALUE, not as an error.
     *
     * @since 3.0.0
     *
     * @param docId The document holding the files, the same id `downloadFile` takes
     */
    DocumentArchiveDocsService.prototype.downloadDocFilesZip = function (docId) {
        return this.apiService.apiCall("GET", BASE_PATH + "/" + encodeURIComponent(docId) + "/files/zip", {}, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * The archive of a property as a TREE, already assembled: the taxonomy with the documents
     * attached and the empty branches pruned. One call, no pagination, no join to do.
     *
     * This is what the dossier screen reads. It replaces the old pair of calls to
     * `/document-template/tree` + this listing, and it asks for `canReadDocumentsList` instead
     * of the `canCreateSettings` the template tree demands.
     *
     * With `includeMockup` the back-end serves the VIRTUAL documents too (`mockup`, NEW-M44), to the
     * role types the legacy dossier shows them to — admin, operator, temporary and shared —: filed
     * when they carry a form, and never in the counters. For any other role it adds nothing and
     * refuses nothing. Only the dossier asks for them (FE-M5): the searches, the deadlines, the
     * exports and the insert read the tree without them, as before. The parameter is left out rather
     * than sent as `false`: the back-end reads the two the same way, and refuses anything but `true`
     * and `false`. A back-end older than NEW-M44 ignores it, and the tree comes without them.
     *
     * @since 3.0.0
     *
     * @param propertyId    Property whose archive is wanted; the back-end resolves the archive
     * @param includeMockup True to have the virtual documents as well
     */
    DocumentArchiveDocsService.prototype.getTreeByProperty = function (propertyId, includeMockup) {
        var params = { propertyId: propertyId };
        if (includeMockup === true) {
            params.includeMockup = "true";
        }
        return this.call("GET", BASE_PATH + "/tree", params);
    };
    /**
     * Same tree as `getTreeByProperty`, addressed by archive instead of by property.
     *
     * The endpoint takes exactly ONE of the two, so they are two methods rather than one with
     * a pair of optional arguments: an object carrying both is a 400, and that is a mistake
     * worth making impossible to write.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.getTreeByArchive = function (documentArchiveId) {
        return this.call("GET", BASE_PATH + "/tree", { documentArchiveId: documentArchiveId });
    };
    /**
     * The archive's two curated name lists, the tenants and the archiving interventions
     * (@see DocumentArchiveNameLists), for the dialogs that manage them (EXT-20).
     *
     * It replaces `GET document-archives/:id` for that purpose: the whole legacy archive, read for
     * two of its fields from a route that never checks the caller may reach that archive. Here the
     * two lists come alone, behind the archive check of this module.
     *
     * Platform staff only: a role type other than admin and operator gets a 403
     * (`errors.not_authorized`) before anything is read. A property without an administrator
     * company gets a 404, as on `/tree`.
     *
     * Errors are NOT handled here (@see call): the caller notifies the user.
     *
     * @since 3.0.0
     *
     * @param documentArchiveId Archive whose lists are wanted
     */
    DocumentArchiveDocsService.prototype.getNameLists = function (documentArchiveId) {
        return this.call("GET", BASE_PATH + "/name-lists", { documentArchiveId: documentArchiveId });
    };
    /**
     * The document types a new document can be filed as in an archive, with the mapping elements it
     * can be filed against (EXT-06). @see DocumentArchiveInsertableTypes
     *
     * The whole live taxonomy, the types never archived in the property included: what the insert
     * dialog of the dossier offers, where the archive tree only carries the types that already have a
     * document.
     *
     * Role types admin, operator, master and collaborator only: any other gets a 403
     * (`errors.not_authorized`) before anything is read. A property without an administrator company
     * gets a 404, as on `/tree`.
     *
     * Errors are NOT handled here (@see call): the caller notifies the user.
     *
     * @since 3.0.0
     *
     * @param documentArchiveId Archive the new document goes into
     */
    DocumentArchiveDocsService.prototype.getInsertableTypes = function (documentArchiveId) {
        return this.call("GET", BASE_PATH + "/insertable-types", { documentArchiveId: documentArchiveId });
    };
    /**
     * The insertable document types of the archive of a property (EXT-06), as the page «Inserimento
     * documenti» reads them: by property, which is what the page knows before the tree answers.
     * @see getInsertableTypes
     *
     * With `insertPage` the back-end applies the rules of the legacy insert page to the areas and the
     * sections (EXT-06-PAG); without it the answer is the whole taxonomy, which the contracts list is
     * composed from. The parameter is left out rather than sent as `false`: the back-end reads the two
     * the same way, and refuses anything but `true` and `false`.
     *
     * Same readers and errors as @see getInsertableTypes. Errors are NOT handled here (@see call): the
     * caller notifies the user.
     *
     * @since 3.0.0
     *
     * @param propertyId Property the new document goes into
     * @param insertPage True for the rules of the insert page
     */
    DocumentArchiveDocsService.prototype.getInsertableTypesByProperty = function (propertyId, insertPage) {
        var params = { propertyId: propertyId };
        if (insertPage) {
            params.insertPage = "true";
        }
        return this.call("GET", BASE_PATH + "/insertable-types", params);
    };
    /**
     * The GENERAL search: every row matching the filters, across the properties the caller may search.
     * @see DocumentArchiveSearchParams
     *
     * One answer with every row, at most 20.000, and no pagination: the page sorts, filters and exports
     * what it receives. Above the cap the back-end refuses with a 400 instead of cutting.
     *
     * Errors are NOT handled here: the page tells them apart, because three of them ask the user for
     * something different (narrow the search, wait, select fewer items).
     *  - 400 with `errors`: a parameter the back-end refused;
     *  - 400 without `errors`: more rows than the cap;
     *  - 403: the role claimed is not held in the company of the request;
     *  - 429: over 60 searches in 5 minutes from the same address, then 60 seconds of block;
     *  - 414 / 431: an address too long for the servers in between, too many ids in the query string.
     *
     * @since 3.0.0
     *
     * @param params The filters, with nothing empty in them
     */
    DocumentArchiveDocsService.prototype.search = function (params) {
        return this.call("GET", BASE_PATH + "/search", params);
    };
    /**
     * The areas, sections and document types the filters of the general search offer, as this caller
     * may filter by them. No parameter: any would be answered 400.
     *
     * Errors are NOT handled here: the page keeps the search usable without these filters.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.searchTaxonomy = function () {
        return this.call("GET", BASE_PATH + "/search/taxonomy");
    };
    /**
     * «Riepilogo documenti» (checklist A11): the documents filed per operator, property, day and type,
     * across every property of the platform. @see DocumentArchiveSummaryParams
     *
     * One answer with every row and no pagination: the page sorts, pages and exports what it receives.
     *
     * Errors are NOT handled here: the page tells them apart.
     *  - 400 with `errors`: a parameter the back-end refused;
     *  - 403: the role claimed is not of type `admin` or `operator`;
     *  - 429: over 60 requests in 5 minutes from the same address, then 60 seconds of block.
     *
     * @since 3.0.0
     *
     * @param params The filters, with nothing empty in them
     */
    DocumentArchiveDocsService.prototype.summary = function (params) {
        return this.call("GET", BASE_PATH + "/summary", params);
    };
    /**
     * «Inserimenti successivi» (checklist A11): for one administrator, the documents filed on each of
     * its active properties with an open balance after the first, per type, from the first filing to
     * the last. @see DocumentArchiveNextInsertionsParams
     *
     * Without an administrator the list is empty, as in the legacy route. Errors are NOT handled here:
     * they are the ones of `summary()`, whose budget of requests this route shares.
     *
     * @since 3.0.0
     *
     * @param params The filters, with nothing empty in them
     */
    DocumentArchiveDocsService.prototype.nextInsertions = function (params) {
        return this.call("GET", BASE_PATH + "/summary/next-insertions", params);
    };
    /**
     * «Documenti in scadenza» (checklist M 15, NEW-M15): one page of the documents expiring from a day
     * on, across the properties the caller may read, or of one property (`search.property`).
     * @see DocumentArchiveDeadlinesParams
     *
     * It replaces the legacy `GET /deadlines/documents` and `GET /deadlines/property/:id/documents`,
     * which read the other archive. The «Scadenze» card of the Home reads `totalCount` of this route.
     *
     * The filters travel as `search[name]`, the keys the back-end reads; an empty one is left out.
     * Errors are NOT handled here: the page tells them apart.
     *  - 400 with `errors`: a parameter the back-end refused;
     *  - 403: a role that is not `admin`, `operator`, `master` or `collaborator`.
     * No per-address limit on this route.
     *
     * @since 3.0.0
     *
     * @param params The window, the order and the filters
     */
    DocumentArchiveDocsService.prototype.deadlines = function (params) {
        var query = {
            start: params.start,
            limit: params.limit,
            sort: params.sort,
            sortVersus: params.sortVersus,
        };
        var search = params.search ? tslib_1.__assign({}, params.search) : {};
        Object.keys(search).forEach(function (name) {
            var value = search[name];
            if (typeof value === "string" && value !== "") {
                query["search[" + name + "]"] = value;
            }
        });
        return this.call("GET", BASE_PATH + "/deadlines", query);
    };
    /**
     * The Excel of «Documenti in scadenza»: every document expiring from today, of the properties the
     * caller may read, or of one property. NO other filter: the back-end answers 400 to any, because
     * the legacy Excels never read the filters of the page either (decision of 21 October 2026).
     *
     * The whole response is observed, as for the zip, and errors are NOT handled here: the page tells
     * them apart by status, and the body of an error is a Blob. Note that ApiService emits a failure
     * without status (network down) as an `Error` VALUE, not as an error.
     *  - 403: a role that may not read the deadlines, or `canExportSubscriptions` missing;
     *  - 429: over 60 files in 5 minutes from the same address, then 60 seconds of block, with the
     *    seconds to wait in `Retry-After`.
     *
     * @since 3.0.0
     *
     * @param [propertyId] The property of the page of one property; omitted for the general Excel
     */
    DocumentArchiveDocsService.prototype.exportDeadlines = function (propertyId) {
        var query = {};
        if (propertyId) {
            query["search[property]"] = propertyId;
        }
        return this.apiService.apiCall("GET", BASE_PATH + "/deadlines/export", query, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * «Elenco documenti immobili» of the supplier page (checklist A 18, NEW-A18): one page of the
     * documents that name the supplier as executor, in the properties the caller may read.
     * @see DocumentArchiveSupplierParams
     *
     * It replaces the legacy `GET /suppliers/:supplierId/documentsv2`, which reads the other archive.
     * The filters travel as `search[name]`, the keys the back-end reads; an empty one is left out,
     * because the list of the back-end is closed and an unknown key is a 400.
     *
     * Errors are NOT handled here: the page tells them apart.
     *  - 400 with `errors`: a parameter the back-end refused;
     *  - 403: `canReadSuppliersList` missing, or a role other than admin, operator, master and
     *    collaborator.
     *
     * @since 3.0.0
     *
     * @param supplierId The supplier, as the suppliers back-end names it
     * @param params     The window, the order and the filters
     */
    DocumentArchiveDocsService.prototype.supplierDocs = function (supplierId, params) {
        var query = {
            start: params.start,
            limit: params.limit,
            sort: params.sort,
            sortVersus: params.sortVersus,
        };
        var search = params.search ? tslib_1.__assign({}, params.search) : {};
        Object.keys(search).forEach(function (name) {
            var value = search[name];
            if (typeof value === "string" && value !== "") {
                query["search[" + name + "]"] = value;
            }
        });
        return this.call("GET", BASE_PATH + "/suppliers/" + encodeURIComponent(supplierId), query);
    };
    /**
     * The Excel of «Elenco documenti immobili»: every document of the supplier the caller may read, in
     * the order of insertion. NO filter: the back-end answers 400 to any, as the legacy Excel read none.
     *
     * The whole response is observed and errors are NOT handled here, as for the other Excels; a
     * failure without status comes as an `Error` VALUE.
     *  - 403: `canExportSuppliers` missing, or a role that may not read the list;
     *  - 429: over the per-address budget, with the seconds to wait in `Retry-After`.
     *
     * @since 3.0.0
     *
     * @param supplierId The supplier, as the suppliers back-end names it
     */
    DocumentArchiveDocsService.prototype.exportSupplierDocs = function (supplierId) {
        return this.apiService.apiCall("GET", BASE_PATH + "/suppliers/" + encodeURIComponent(supplierId) + "/export", {}, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * «Ultimi documenti inseriti» (checklist M 16, NEW-M16): one page of the documents filed in the last
     * 30 days, across the properties the caller may read, or of one property (`propertyId`).
     * @see DocumentArchiveHistoricalParams
     *
     * It replaces the legacy `GET /document-archives/historical-docs`, which reads the other archive.
     * EVERY call stamps the visit of the user, in the collection the legacy stamps: it clears the alert
     * of the Home of both archives.
     *
     * The filters travel as `search[name]`, the keys the back-end reads; an empty one is left out.
     * Errors are NOT handled here: the page tells them apart.
     *  - 400 with `errors`: a parameter the back-end refused;
     *  - 403: a role that does not resolve, or that the user does not hold in the company of the
     *    request.
     * No per-address limit on this route.
     *
     * @since 3.0.0
     *
     * @param params The window, the order, the property and the filters
     */
    DocumentArchiveDocsService.prototype.historicalDocs = function (params) {
        var query = {
            start: params.start,
            limit: params.limit,
            sort: params.sort,
            sortVersus: params.sortVersus,
        };
        if (params.propertyId) {
            query.propertyId = params.propertyId;
        }
        var search = params.search ? tslib_1.__assign({}, params.search) : {};
        Object.keys(search).forEach(function (name) {
            var value = search[name];
            if (typeof value === "string" && value !== "") {
                query["search[" + name + "]"] = value;
            }
        });
        return this.call("GET", BASE_PATH + "/historical-docs", query);
    };
    /**
     * The alert of the Home and the «!» of the menu (NEW-M16-SW): how many documents of «Ultimi
     * documenti inseriti» were filed since the caller's last visit to the list.
     * @see DocumentArchiveHistoricalCheck
     *
     * It replaces the legacy `GET /document-archives/check-historical-docs`, which reads the other
     * archive and answers 410 once it is switched off. NO parameter: the back-end answers 400 to any.
     * Writes no visit. Errors are NOT handled here:
     *  - 403: a role the list refuses, or one the user does not hold in the company of the request.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.historicalDocsCheck = function () {
        return this.call("GET", BASE_PATH + "/historical-docs/check");
    };
    /**
     * The documents of the company of the request, for the card «Documenti» of the Home (NEW-M21).
     * @see DocumentArchiveCompanyTotals
     *
     * It replaces the legacy `GET /document-archives/company-total-docs`, which reads the other archive.
     * NO parameter, `companyId` included: the back-end answers 400 to any, and reads the company of the
     * request. Errors are NOT handled here:
     *  - 403: a role other than `master` and `collaborator`, or `canReadDocumentsList` missing.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.companyTotals = function () {
        return this.call("GET", BASE_PATH + "/company-totals");
    };
    /**
     * The Excel of «Ultimi documenti inseriti»: every document filed in the last 30 days, the latest
     * first, of the properties the caller may read, or of one property only. NO filter of the page: the
     * back-end answers 400 to any parameter but `propertyId`. Writes no visit.
     *
     * The whole response is observed, as for the zip, and errors are NOT handled here: the page tells
     * them apart by status, and the body of an error is a Blob. Note that ApiService emits a failure
     * without status (network down) as an `Error` VALUE, not as an error.
     *  - 403: as the list;
     *  - 429: over 60 files in 5 minutes from the same address, then 60 seconds of block, with the
     *    seconds to wait in `Retry-After`.
     *
     * @since 3.0.0
     *
     * @param [propertyId] The property of the page of one property; omitted for the general Excel
     */
    DocumentArchiveDocsService.prototype.exportHistoricalDocs = function (propertyId) {
        var query = {};
        if (propertyId) {
            query.propertyId = propertyId;
        }
        return this.apiService.apiCall("GET", BASE_PATH + "/historical-docs/export", query, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * The Excel of «Esporta inseriti» or «Esporta mancanti» of the dossier (NEW-M7), for one property:
     *  - `inserted`: the rows present in the archive, under the numbered headings of the dossier, the
     *    expired ones at the bottom of their block; same 8 columns as the legacy `insertedDocs2025`;
     *  - `missing`: one line per mandatory row still missing; same 2 columns as the legacy
     *    `missingDocs`.
     * They replace `GET properties/:id/export/insertedDocs2025` and `/export/missingDocs`, which read
     * the other archive. Until the switch these read THIS archive only: rows filed in the legacy one
     * after the copy of 10 August 2026 are not in them.
     *
     * `propertyId` is the only parameter: the back-end answers 400 to any other. The back-end checks
     * `canExportProperties` and the perimeter of `/tree`, NOT the plan: hiding «Mancanti» from the free
     * plans is up to the page.
     *
     * The whole response is observed, as for the other Excels, and errors are NOT handled here: the
     * page tells them apart by status, and the body of an error is a Blob. Note that ApiService emits a
     * failure without status (network down) as an `Error` VALUE, not as an error.
     *  - 403: `canExportProperties` missing, or a property outside the company of the request;
     *  - 404: no such property or archive, or a property with no administering company;
     *  - 429: over 60 files in 5 minutes from the same address, shared by the two Excels, with the
     *    seconds to wait in `Retry-After`.
     *
     * @since 3.0.0
     *
     * @param target     Which of the two Excels
     * @param propertyId The property of the dossier
     */
    DocumentArchiveDocsService.prototype.exportPropertyDocs = function (target, propertyId) {
        return this.apiService.apiCall("GET", BASE_PATH + "/export/" + target, { propertyId: propertyId }, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * The rows of «Esporta RAS» of the dossier (NEW-M7), which the page composes into a PDF. It
     * replaces `GET properties/:id/ras-docs`, which reads the other archive; same envelope, the page
     * reads `data.status` and `data.rasDocs`. @see DocumentArchiveRasDoc
     *
     * `propertyId` is the only parameter. Titles come in the language of the request. No per-address
     * limit. The back-end asks `canReadDocumentsList`, the key of `/tree`, and NOT
     * `canExportProperties`: it follows the menu, which shows the entry on the plan alone. Errors are
     * NOT handled here:
     *  - 403: a property outside the company of the request;
     *  - 404: no such property or archive.
     *
     * @since 3.0.0
     *
     * @param propertyId The property of the dossier
     */
    DocumentArchiveDocsService.prototype.rasDocs = function (propertyId) {
        return this.call("GET", BASE_PATH + "/ras", { propertyId: propertyId });
    };
    /**
     * The parts of the dossier zip of a property (NEW-M17), for «Scarica archivio fascicolo documentale»
     * and «Esporta file» on the 3.0.0 archive. It replaces `GET properties/:id/export-file`, which builds
     * every zip of the other archive into the public `/export` folder and answers their paths: here
     * nothing is built until a part is asked for (@see downloadDossierPart).
     *
     * `propertyId` is the only parameter. The back-end asks `canExportProperties`, the key of the legacy
     * route, plus the rule of the archive of `/tree`. Errors are NOT handled here: the dialog tells them
     * apart by status.
     *  - 403: `canExportProperties` missing, or a property outside the company of the request;
     *  - 404: a property without archive, or without administering company;
     *  - 429: over 30 requests in 10 minutes from the same address, on ONE counter for the list and the
     *    parts, with the seconds to wait in `Retry-After`.
     *
     * @since 3.0.0
     *
     * @param propertyId The property of the dossier
     */
    DocumentArchiveDocsService.prototype.dossierParts = function (propertyId) {
        return this.call("GET", BASE_PATH + "/export/dossier", { propertyId: propertyId });
    };
    /**
     * The zip of one part of the dossier (NEW-M17), streamed by the back-end with the session, never
     * from a public path. The part is named as the list names it (@see dossierParts): "1"…"N", or
     * "contracts".
     *
     * The whole response is observed, as for the other zips: the name travels in `Content-Disposition`
     * and the number of files left out in `X-Archive-Skipped-Files`. Errors are NOT handled here: the
     * dialog tells them apart by status, and the body of an error is a Blob. Note that ApiService emits
     * a failure without status (network down, connection cut once the zip had started) as an `Error`
     * VALUE, not as an error.
     *  - 403: as the list;
     *  - 404: no archive, no files, or a part the dossier no longer has: the back-end composes the
     *    dossier again on every request, so a part can move between the list and the download;
     *  - 410: the archive names files, but the disk holds none of them;
     *  - 429: as the list, on the same counter.
     *
     * The zip arrives whole in the memory of the browser before it is saved: a part holds about 500 MB
     * of files, and more when the files are large.
     *
     * @since 3.0.0
     *
     * @param propertyId The property of the dossier
     * @param part       The part, as the list names it
     */
    DocumentArchiveDocsService.prototype.downloadDossierPart = function (propertyId, part) {
        return this.apiService.apiCall("GET", BASE_PATH + "/export/dossier/" + encodeURIComponent(part), { propertyId: propertyId }, { responseType: "blob", observe: "response" }, undefined, undefined, API_VERSION);
    };
    /**
     * «Cestino documenti» (FEX-01): the properties holding deleted documents of THIS archive, one page
     * at a time, in the legacy shape (@see DeletedDocsSummary). The legacy page reads the other
     * archive, through `DeletedDocsService`.
     *
     * Without `propertyId` the back-end groups every deleted document of the platform, and pays it
     * again on every page: callers must never prefetch pages. Every counter is computed on the
     * filtered set.
     *
     * Errors are NOT handled here: a 403 (`canRestoreDocuments` missing, or a role not of type
     * `admin`) means "not authorized", anything else a failed load.
     *
     * @since 3.0.0
     *
     * @param params The window, the sort and the filters
     */
    DocumentArchiveDocsService.prototype.deletedDocsSummary = function (params) {
        var query = {
            start: params.start,
            limit: params.limit,
            sort: params.sort,
            sortVersus: params.sortVersus,
        };
        if (params.propertyId) {
            query.propertyId = params.propertyId;
        }
        // Only "true" travels: the back-end reads anything else as off
        if (params.onlyManual === true) {
            query.onlyManual = "true";
        }
        if (params.onlyWithContent === true) {
            query.onlyWithContent = "true";
        }
        return this.call("GET", BASE_PATH + "/deleted-docs/summary", query);
    };
    /**
     * «Cestino documenti» (FEX-01): every deleted document of one property of THIS archive, manual
     * deletions first, at most 2.000 rows (`truncated` when the cap cut the list). Not paginated.
     *
     * The rows are not the legacy ones: @see DocumentArchiveDeletedDoc. Errors are NOT handled here,
     * as for `deletedDocsSummary()`.
     *
     * @since 3.0.0
     *
     * @param propertyId The property, as the summary named it
     * @param [scope] The two filters, sent only when they are on
     */
    DocumentArchiveDocsService.prototype.deletedDocsByProperty = function (propertyId, scope) {
        var query = {};
        if (scope && scope.onlyManual === true) {
            query.onlyManual = "true";
        }
        if (scope && scope.onlyWithContent === true) {
            query.onlyWithContent = "true";
        }
        return this.call("GET", BASE_PATH + "/deleted-docs/property/" + encodeURIComponent(propertyId), query);
    };
    /**
     * «Cestino documenti» (FEX-01): restore deleted documents of THIS archive, of any property, as the
     * legacy `PATCH document-archives/restore-docs` does on the other one: each row comes back visible
     * in the dossier, with the side effects of its deletion undone (empty row retired, counters, charge,
     * delivery note).
     *
     * At most 50 ids per call (400 above it): callers split bigger selections themselves and send the
     * blocks one after the other. The answer is the legacy report: what was restored, what was skipped
     * and why (`not_found`, `not_deleted`), and the side effects to check by hand. Idempotent: an id
     * already restored comes back as `not_deleted`, with no side effect.
     *
     * Errors are NOT handled here: a 403 means the role is not of type `admin`.
     *
     * @since 3.0.0
     *
     * @param docIds Ids of the deleted rows (1-50)
     */
    DocumentArchiveDocsService.prototype.restoreDeletedDocs = function (docIds) {
        return this.call("PATCH", BASE_PATH + "/restore-docs", { docIds: docIds });
    };
    /**
     * Turns the typed filters into the query string the back-end expects.
     *
     * Booleans become the literals "true"/"false" because they travel as query parameters,
     * and an omitted filter is left `undefined` on purpose: ApiService drops undefined keys,
     * which is what makes the back-end apply its own default instead of a value guessed
     * here.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.buildListParams = function (params) {
        var query = {
            documentArchiveId: params.documentArchiveId,
            documentTemplateDocId: params.documentTemplateDocId,
            structureObjectId: params.structureObjectId,
            contract: this.asFlag(params.contract),
            expired: this.asFlag(params.expired),
            includeDeleted: this.asFlag(params.includeDeleted),
            includeMockup: this.asFlag(params.includeMockup),
        };
        if (typeof params.skip === "number") {
            query.skip = String(params.skip);
        }
        if (typeof params.limit === "number") {
            query.limit = String(params.limit);
        }
        return query;
    };
    /**
     * `undefined` for an unset filter, "true"/"false" for a set one.
     *
     * `false` is a meaningful filter (only non expired documents, for instance) and must
     * reach the back-end, so it cannot be collapsed with "not asked for".
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.asFlag = function (value) {
        return typeof value === "boolean" ? (value ? "true" : "false") : undefined;
    };
    /**
     * Thin wrapper around ApiService pinning the v2 version on every call.
     *
     * On a network failure (status 0) ApiService emits an Error as a NEXT value instead of
     * erroring: left as is, the caller would treat it as an empty successful response and
     * silently show an empty dossier. It is turned back into a real error here.
     *
     * @since 3.0.0
     */
    DocumentArchiveDocsService.prototype.call = function (method, path, params) {
        return this.apiService
            .apiCall(method, path, params, undefined, undefined, undefined, API_VERSION)
            .pipe(map(function (res) {
            if (res instanceof Error) {
                throw res;
            }
            return res;
        }));
    };
    DocumentArchiveDocsService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function DocumentArchiveDocsService_Factory() { return new DocumentArchiveDocsService(i0.ɵɵinject(i1.ApiService), i0.ɵɵinject(i2.HelperService)); }, token: DocumentArchiveDocsService, providedIn: "root" });
    return DocumentArchiveDocsService;
}());
export { DocumentArchiveDocsService };
